import React, { useState, useEffect, useRef } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { ChallengeTableInspectionsProgramsVersionsItem, ChallengeTableProgramVersionItem } from '../../api/types/dtos/challenge/ChallengeDto';
import { Form, FormAnswers } from '../../api/types/forms';
import DynamicFormRenderer from '../forms/DynamicFormRenderer';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useOrganization } from '../../contexts/OrganizationContext';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { answerService, AnswerDto } from '../../api/services/answer.service';
import { formService, FormDto } from '../../api/services/form.service';

// Define the type for the exposed submit function from DynamicFormRenderer
interface DynamicFormRendererHandle {
  submit: () => void;
}

interface InspectionProgramVersionItemModalProps {
  open: boolean;
  onClose: () => void;
  item: ChallengeTableInspectionsProgramsVersionsItem | null;
  onSave: (updated: Partial<ChallengeTableInspectionsProgramsVersionsItem>) => void;
  formTemplateJsonProp?: string;
  programVersionItem?: ChallengeTableProgramVersionItem;
  ownerName?: string;
}

const InspectionProgramVersionItemModal: React.FC<InspectionProgramVersionItemModalProps> = ({
  open,
  onClose,
  item,
  onSave,
  formTemplateJsonProp,
  programVersionItem,
  ownerName,
}) => {
  const [form, setForm] = useState<Partial<ChallengeTableInspectionsProgramsVersionsItem>>(item || {});
  const [parsedFormDefinition, setParsedFormDefinition] = useState<Form | null>(null);
  const [initialFormAnswers, setInitialFormAnswers] = useState<FormAnswers | null>(null);
  const [displayFormRendererMode, setDisplayFormRendererMode] = useState(false);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFileUploading, setIsFileUploading] = useState(false);
  const [isLoadingAnswers, setIsLoadingAnswers] = useState(false);
  const [fetchedAnswers, setFetchedAnswers] = useState<FormAnswers | null>(null);

  const submitRhfFormRef = useRef<DynamicFormRendererHandle | null>(null);
  const { selectedOrganization } = useOrganization();

  useEffect(() => {
    if (item && open) {
      // Reset state
      setForm({ ...item, value: item.value || '' });
      setIsLoadingTemplate(true);
      setDisplayFormRendererMode(false);
      setParsedFormDefinition(null);
      setInitialFormAnswers(null);
      setFetchedAnswers(null);
      setIsLoadingAnswers(false);

      // Priority 1: Use new forms system if formId exists
      if (programVersionItem?.formId && selectedOrganization) {
        // Fetch form definition and answers in parallel
        Promise.all([
          formService.getById(selectedOrganization.id, programVersionItem.formId),
          answerService.getAnswers(
            selectedOrganization.id,
            item.inspectionProgramId,
            item.programVersionId,
            item.id
          )
        ]).then(([formResponse, answersResponse]: [any, any]) => {
          // Convert FormDto to Form interface for DynamicFormRenderer
          const formDto: FormDto = formResponse.data;
          const formDef: Form = {
            form_id: formDto.id,
            organizationId: formDto.organizationId,
            stringId: formDto.stringId,
            connector: formDto.connector,
            title: formDto.title,
            description: formDto.description,
            questions: formDto.questions.map(q => ({
              id: q.stringId, // Use stringId as legacy ID for field names & condition lookups
              uuid: q.id, // Use backend UUID for answer storage
              text: q.text,
              help_text: q.helpText,
              type: q.type.toLowerCase() as any, // Convert MULTISELECT -> multiselect
              options: q.options,
              mandatory: q.mandatory,
              placeholder: q.placeholder,
              default_value: q.defaultValue,
              validation: q.validation ? JSON.parse(q.validation) : undefined,
              display_logic: {
                condition_type: q.displayLogicConditionType.toLowerCase() as any,
                conditions: q.displayLogicConditions ? JSON.parse(q.displayLogicConditions) : undefined,
                source_multiselect_id: q.displayLogicSourceMultiselectId,
                context_label_template: q.displayLogicContextLabelTemplate
              },
              attachments: []
            })) as any
          };
          setParsedFormDefinition(formDef);
          setDisplayFormRendererMode(true);

          // Process answers - parse JSON values if they're strings
          const answersData = answersResponse.data || [];
          const mappedAnswers: FormAnswers = answersData.map((a: AnswerDto) => {
            let parsedValue = a.value;
            // If value is a string that looks like JSON, parse it
            if (typeof parsedValue === 'string' && (parsedValue.startsWith('[') || parsedValue.startsWith('{'))) {
              try {
                parsedValue = JSON.parse(parsedValue);
              } catch {
                // Keep original value if parsing fails
              }
            }
            return {
              answerId: a.id,
              questionId: a.questionId,
              value: parsedValue,
              forEachContextValue: a.forEachContextValue,
              parentContextId: a.parentContextId
            };
          });
          setFetchedAnswers(mappedAnswers);
          setInitialFormAnswers(mappedAnswers); // Empty array is valid - no answers yet
          setIsLoadingAnswers(false);
          setIsLoadingTemplate(false);
        }).catch(error => {
          console.error('Failed to fetch form or answers:', error);
          // Fall back to legacy if new system fails
          if (formTemplateJsonProp) {
            try {
              const parsedDef = JSON.parse(formTemplateJsonProp) as Form;
              setParsedFormDefinition(parsedDef);
              setDisplayFormRendererMode(true);
              if (item.value && typeof item.value === 'string') {
                try {
                  const parsedAns = JSON.parse(item.value) as FormAnswers;
                  setInitialFormAnswers(parsedAns);
                } catch {
                  setInitialFormAnswers([]);
                }
              } else {
                setInitialFormAnswers([]);
              }
            } catch (e) {
              setDisplayFormRendererMode(false);
            }
          } else {
            // No form at all - use simple value field
            let initialValue = item.value;
            if (initialValue === null || initialValue === undefined || initialValue === '') {
              initialValue = new Date().toISOString().replace(/\.(\d{3})Z$/, '.$1000Z');
            }
            setForm({ ...item, value: initialValue });
          }
          setIsLoadingAnswers(false);
          setIsLoadingTemplate(false);
        });
        return; // Exit early, don't continue to legacy logic
      }

      // Priority 2: Legacy form system (measurementTemplate)
      if (formTemplateJsonProp) {
        try {
          const parsedDef = JSON.parse(formTemplateJsonProp) as Form;
          setParsedFormDefinition(parsedDef);
          setDisplayFormRendererMode(true);
          if (item.value && typeof item.value === 'string') {
            try {
              const parsedAns = JSON.parse(item.value) as FormAnswers;
              setInitialFormAnswers(parsedAns);
            } catch (e) {
              console.warn('item.value is not valid FormAnswers JSON:', item.value, e);
              setInitialFormAnswers([]);
            }
          } else {
            setInitialFormAnswers([]);
          }
        } catch (e) {
          console.error('Failed to parse formTemplateJsonProp:', e);
          setDisplayFormRendererMode(false);
        }
      } else {
        // Priority 3: Simple value field (no form)
        setDisplayFormRendererMode(false);
        let initialValue = item.value;
        if (initialValue === null || initialValue === undefined || initialValue === '') {
          initialValue = new Date().toISOString().replace(/\.(\d{3})Z$/, '.$1000Z');
        }
        setForm({ ...item, value: initialValue });
      }
      setIsLoadingTemplate(false);
    } else {
      // Modal closed
      setForm({});
      setDisplayFormRendererMode(false);
      setParsedFormDefinition(null);
      setInitialFormAnswers(null);
      setFetchedAnswers(null);
      setIsLoadingAnswers(false);
      setIsLoadingTemplate(false);
    }
  }, [item, formTemplateJsonProp, open, programVersionItem?.formId, selectedOrganization]);

  if (!item && open) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setForm(f => ({
      ...f,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleDynamicFormSubmit = (answers: FormAnswers) => {
    try {
      const stringifiedAnswers = JSON.stringify(answers);
      setForm(f => ({ ...f, value: stringifiedAnswers }));
    } catch (error) {
      console.error("Error stringifying form answers:", error);
    }
  };

  const handleMainSave = async () => {
    setIsSubmitting(true);
    if (displayFormRendererMode && submitRhfFormRef.current) {
      submitRhfFormRef.current.submit();
    } else {
      onSave(form);
      onClose();
    }
    if (!displayFormRendererMode) {
      setIsSubmitting(false);
    }
  };

  const processAndSaveDynamicForm = async (answers: FormAnswers) => {
    if (!item || !selectedOrganization) {
      console.error('Missing required data for saving answers');
      return;
    }

    setIsSubmitting(true);
    try {
      // If formId exists, use new answers table system
      if (programVersionItem?.formId) {
        // Convert FormAnswers to CreateAnswerRequest format
        const createAnswerRequests = answers.map(ans => ({
          questionId: ans.questionId,
          value: ans.value,
          forEachContextValue: ans.forEachContextValue,
          parentContextId: ans.parentContextId
        }));

        await answerService.saveAnswers(
          selectedOrganization.id,
          item.inspectionProgramId,
          item.programVersionId,
          item.id,
          createAnswerRequests
        );

        // Save item state (comments, dates, etc.) but NOT value
        onSave({ ...form });
      } else {
        // Legacy system: save to item.value
        const stringifiedAnswers = JSON.stringify(answers);
        const updatedFormState = { ...form, value: stringifiedAnswers };
        onSave(updatedFormState);
      }
      onClose();
    } catch (error) {
      console.error("Error saving form answers:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={displayFormRendererMode ? 'md' : 'sm'} fullWidth>
      <DialogTitle>
        {programVersionItem?.cpNumber}: {programVersionItem?.name}
      </DialogTitle>
      {ownerName && (
        <Box sx={{ px: 3, pb: 1 }}>
          <Typography variant="subtitle1" color="text.secondary">{ownerName}</Typography>
        </Box>
      )}
      <DialogContent dividers>
        <TextField
          label="Comentarios"
          name="comments"
          value={form.comments || ''}
          onChange={handleChange}
          fullWidth
          margin="normal"
          multiline
          disabled={isSubmitting}
        />
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
          <DatePicker
            label="Fecha de inicio"
            value={form.startDate ? new Date(form.startDate) : null}
            onChange={(date) => {
              setForm(f => ({ ...f, startDate: date ? new Date(date.setSeconds(0, 0)).toISOString().replace(/\.(\d{3})Z$/, '.$1000Z') : null }));
            }}
            slotProps={{ textField: { fullWidth: true, margin: 'normal', variant: 'outlined', placeholder: '' } }}
          />
          <DatePicker
            label="Fecha de finalización"
            value={form.endDate ? new Date(form.endDate) : null}
            onChange={(date) => {
              setForm(f => ({ ...f, endDate: date ? new Date(date.setSeconds(0, 0)).toISOString().replace(/\.(\d{3})Z$/, '.$1000Z') : null }));
            }}
            slotProps={{ textField: { fullWidth: true, margin: 'normal', variant: 'outlined', placeholder: '' } }}
          />
        </LocalizationProvider>
        <FormControl fullWidth margin="normal">
          <InputLabel id="state-select-label">Estado</InputLabel>
          <Select
            labelId="state-select-label"
            value={!form.isActive ? 'disabled' : form.isApproved ? 'approved' : 'enabled'}
            label="Estado"
            disabled={isSubmitting}
            onChange={(e) => {
              const val = e.target.value;
              const nowIso = new Date().toISOString().replace(/\.(\d{3})Z$/, '.$1000Z');
              // Track previous state
              const prevIsActive = form.isActive;
              const prevIsApproved = form.isApproved;
              if (val === 'disabled') {
                setForm(f => ({ ...f, isActive: false, isApproved: false }));
              } else if (val === 'enabled') {
                // Moved to En Proceso
                setForm(f => ({
                  ...f,
                  isActive: true,
                  isApproved: false,
                  startDate: f.startDate ? f.startDate : nowIso
                }));
              } else if (val === 'approved') {
                // Moved to Completado
                if (!prevIsActive) {
                  // From Pendiente to Completado: set both start and end date if empty
                  setForm(f => ({
                    ...f,
                    isActive: true,
                    isApproved: true,
                    startDate: f.startDate ? f.startDate : nowIso,
                    endDate: f.endDate ? f.endDate : nowIso
                  }));
                } else if (prevIsActive && !prevIsApproved) {
                  // From En Proceso to Completado: set only end date if empty
                  setForm(f => ({
                    ...f,
                    isActive: true,
                    isApproved: true,
                    endDate: f.endDate ? f.endDate : nowIso
                  }));
                } else {
                  setForm(f => ({ ...f, isActive: true, isApproved: true }));
                }
              }
            }}
          >
            <MenuItem value="disabled">Pendiente</MenuItem>
            <MenuItem value="enabled">En Proceso</MenuItem>
            <MenuItem value="approved">Completado</MenuItem>
          </Select>
        </FormControl>

        {isLoadingTemplate || isLoadingAnswers ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 100 }}>
            <CircularProgress />
          </Box>
        ) : displayFormRendererMode && parsedFormDefinition ? (
          <DynamicFormRenderer
            key={item?.id || item?.programVersionItemId || Math.random()}
            ref={submitRhfFormRef}
            formDefinition={parsedFormDefinition}
            initialAnswers={initialFormAnswers}
            onSubmit={processAndSaveDynamicForm}
            onCancel={onClose}
            organizationId={selectedOrganization?.id || ''}
            programId={item?.programVersionId || ''}
            inspectionProgramId={item?.inspectionProgramId || ''}
            itemId={item?.id || item?.programVersionItemId || ''}
            onUploadingChange={setIsFileUploading}
          />
        ) : (
          !isLoadingTemplate && (
            <TextField
              label="Valor"
              name="value"
              value={form.value || ''}
              onChange={handleChange}
              fullWidth
              margin="normal"
              multiline={typeof form.value === 'string' && form.value.length > 50}
              disabled={isSubmitting}
            />
          )
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>Cancelar</Button>
        <Button
          onClick={handleMainSave}
          variant="contained"
          color="primary"
          disabled={isSubmitting || isFileUploading || (displayFormRendererMode && !parsedFormDefinition)}
        >
          {isSubmitting || isFileUploading ? <CircularProgress size={24} color="inherit" /> : "Guardar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InspectionProgramVersionItemModal; 